import React from 'react';
import { DateTextInput } from './DateTextInput.jsx';
import Select from './Select.jsx';

const shell = 'inline-flex min-h-[2rem] items-center gap-1.5 rounded-md border border-border bg-surface-raised px-2 py-1 text-[0.72rem] text-text-muted';
const label = 'text-[0.62rem] uppercase tracking-[0.06em] text-text-dim';
const smallButton = 'rounded-md border border-border bg-surface-el px-2 py-[0.25rem] text-[0.66rem] font-semibold uppercase tracking-[0.05em] text-text-muted hover:border-accent-gold hover:text-accent-gold disabled:cursor-not-allowed disabled:opacity-55';
const dateInput = 'h-[1.65rem] w-[8.2rem] rounded-md border border-border-strong bg-surface-el px-2 text-[0.72rem] text-text focus:border-accent-gold focus:outline-none';

function optionLabel(item, fallback) {
  return item?.name || item?.label || fallback;
}

function settingValue(value) {
  if (value == null) return null;
  if (typeof value === 'string') return value.trim() || null;
  if (typeof value === 'number') return String(value);
  if (typeof value === 'object') {
    return settingValue(value.value)
      || settingValue(value.code)
      || settingValue(value.label)
      || settingValue(value.display);
  }
  return String(value).trim() || null;
}

function cleanScopeId(value) {
  const trimmed = String(value ?? '').trim();
  return trimmed || null;
}

function parseScopeValue(value) {
  const raw = String(value || '');
  if (!raw) return { bookId: null, shelfId: null, basis: 'lens-selector' };
  const [kind, ...rest] = raw.split(':');
  const id = rest.join(':');
  if (kind === 'shelf' && id) return { bookId: null, shelfId: id, basis: 'lens-selector' };
  if (kind === 'book' && id) return { bookId: id, shelfId: null, basis: 'lens-selector' };
  return { bookId: null, shelfId: null, basis: 'lens-selector' };
}

function bookShelfId(catalog, bookId) {
  const normalizedBookId = cleanScopeId(bookId);
  if (!normalizedBookId) return null;
  const links = Array.isArray(catalog?.bookLinks) ? catalog.bookLinks : [];
  return links.find((link) => link?.bookId === normalizedBookId)?.shelfId || null;
}

function booksForShelf(catalog, shelfId, books) {
  const normalizedShelfId = cleanScopeId(shelfId);
  if (!normalizedShelfId) return [];
  const links = Array.isArray(catalog?.bookLinks) ? catalog.bookLinks : [];
  const bookIds = new Set(
    links
      .filter((link) => link?.shelfId === normalizedShelfId)
      .map((link) => link.bookId)
      .filter(Boolean),
  );
  return books.filter((book) => bookIds.has(book.id));
}

function unshelvedBooks(catalog, books) {
  const explicitIds = new Set(Array.isArray(catalog?.unshelvedBookIds) ? catalog.unshelvedBookIds : []);
  if (explicitIds.size > 0) return books.filter((book) => explicitIds.has(book.id));
  const linkedIds = new Set(
    (Array.isArray(catalog?.bookLinks) ? catalog.bookLinks : [])
      .map((link) => link?.bookId)
      .filter(Boolean),
  );
  return books.filter((book) => !linkedIds.has(book.id));
}

function bookCountLabel(count) {
  return `${count} ${count === 1 ? 'book' : 'books'}`;
}

function dateToMs(value) {
  const ms = Date.parse(value || '');
  return Number.isFinite(ms) ? ms : null;
}

function percentWithin(value, min, max) {
  const valueMs = dateToMs(value);
  const minMs = dateToMs(min);
  const maxMs = dateToMs(max);
  if (valueMs == null || minMs == null || maxMs == null || maxMs <= minMs) return 0;
  return Math.max(0, Math.min(100, ((valueMs - minMs) / (maxMs - minMs)) * 100));
}

export function buildLedgerUrlWithBookScope(baseUrl, selection = {}) {
  const trimmedBase = String(baseUrl ?? '').trim();
  if (!trimmedBase) return trimmedBase;

  const bookId = cleanScopeId(selection.bookId);
  const shelfId = cleanScopeId(selection.shelfId);

  try {
    const fallbackOrigin = typeof window !== 'undefined'
      ? window.location.origin
      : 'https://ledger.cleic.com';
    const url = new URL(trimmedBase, fallbackOrigin);
    if (bookId) url.searchParams.set('bookId', bookId);
    else url.searchParams.delete('bookId');
    if (shelfId) url.searchParams.set('shelfId', shelfId);
    else url.searchParams.delete('shelfId');

    if (typeof window !== 'undefined') {
      const channel = new URLSearchParams(window.location.search).get('cleic_app_channel');
      if (channel) url.searchParams.set('cleic_app_channel', channel);
    }

    return url.toString();
  } catch {
    return trimmedBase;
  }
}

export function LensBookShelfScopeSelector({
  catalog,
  selection,
  loading = false,
  error = null,
  onChange,
}) {
  const books = Array.isArray(catalog?.books) ? catalog.books : [];
  const shelves = Array.isArray(catalog?.flatShelves) ? catalog.flatShelves : [];
  const selectedBookId = selection?.bookId || '';
  const selectedShelfId = selection?.shelfId || '';
  const selectedBook = books.find((book) => book.id === selectedBookId) || null;
  const selectedShelf = shelves.find((shelf) => shelf.id === selectedShelfId) || null;
  const selectedBookShelf = selectedBook ? shelves.find((shelf) => shelf.id === bookShelfId(catalog, selectedBook.id)) : null;
  const activeKind = selectedShelfId ? 'shelf' : selectedBookId ? 'book' : 'none';
  const value = selectedShelfId ? `shelf:${selectedShelfId}` : selectedBookId ? `book:${selectedBookId}` : '';
  const disabled = loading || (books.length === 0 && shelves.length === 0 && !selectedBookId && !selectedShelfId);
  const shelfOptions = shelves.flatMap((shelf) => {
    const shelfBooks = booksForShelf(catalog, shelf.id, books);
    return [
      {
        value: `shelf:${shelf.id}`,
        label: `Shelf scope: ${shelf.name} (${bookCountLabel(shelfBooks.length)})`,
      },
      ...shelfBooks.map((book) => ({
        value: `book:${book.id}`,
        label: `Book in ${shelf.name}: ${book.name}`,
      })),
    ];
  });
  const unshelvedOptions = unshelvedBooks(catalog, books).map((book) => ({
    value: `book:${book.id}`,
    label: `Book scope: ${book.name}`,
  }));
  const scopeOptions = [
    {
      value: '',
      label: loading
        ? 'Scopes...'
        : activeKind === 'none'
          ? 'No scope - hold at zero'
          : 'Clear scope - hold at zero',
      disabled: false,
    },
    ...(selectedShelfId && !selectedShelf ? [{ value: `shelf:${selectedShelfId}`, label: 'Selected Shelf scope' }] : []),
    ...(selectedBookId && !selectedBook ? [{ value: `book:${selectedBookId}`, label: 'Selected Book scope' }] : []),
    ...shelfOptions,
    ...unshelvedOptions,
  ];
  const activeLabel = activeKind === 'shelf'
    ? optionLabel(selectedShelf, 'Shelf selected')
    : activeKind === 'book'
      ? optionLabel(selectedBook, 'Book selected')
      : 'No Book/Shelf scope selected';
  const activeSummary = activeKind === 'shelf'
    ? 'Shelf'
    : activeKind === 'book'
      ? 'Book'
      : 'No scope';
  const title = error
    ? `Ledger Books/Shelves unavailable: ${error}`
    : activeKind === 'shelf'
      ? `Lens analytical scope: Shelf ${activeLabel}; includes all Books Ledger assigns to this Shelf.`
      : activeKind === 'book'
        ? `Lens analytical scope: Book ${activeLabel}${selectedBookShelf ? ` in Shelf ${selectedBookShelf.name}` : ''}.`
        : 'Lens analytical scope: no Book or Shelf selected.';

  function changeScope(event) {
    onChange?.(parseScopeValue(event.target.value));
  }

  return (
    <div className={shell} title={title} data-ux-pattern="lens-book-shelf-global-filter-control">
      <span className={label}>Book/Shelf</span>
      <Select
        aria-label="Lens Book/Shelf scope"
        className="max-w-[250px] min-w-[180px]"
        value={value}
        onChange={changeScope}
        disabled={disabled}
        compact
      >
        {scopeOptions.map((option) => (
          <option key={`${option.value || 'empty'}-${option.label}`} value={option.value} disabled={option.disabled}>
            {option.label}
          </option>
        ))}
      </Select>
      <span className="rounded-md border border-border bg-surface-el px-1.5 py-[0.2rem] text-[0.62rem] font-semibold uppercase tracking-[0.05em] text-text-dim">
        {activeSummary}
      </span>
    </div>
  );
}

export function LensReportingCurrencyControl({ sourceState, handoffContext, catalog, selection }) {
  const books = Array.isArray(catalog?.books) ? catalog.books : [];
  const selectedBook = books.find((book) => book.id === selection?.bookId) || null;
  const hasScope = Boolean(selection?.bookId || selection?.shelfId);
  const settings = sourceState?.bookSettings || sourceState?.meta?.bookSettings || null;
  const defaultCurrency = settingValue(settings?.defaultCurrency)
    || settingValue(selectedBook?.defaultCurrency);
  const contractReportingCurrency = settingValue(settings?.reportingCurrency);
  const handoffCurrency = settingValue(handoffContext?.reportingCurrency);
  const reportingCurrency = contractReportingCurrency || defaultCurrency;
  const currencyLabel = handoffCurrency && !contractReportingCurrency
    ? `${handoffCurrency} handoff`
    : reportingCurrency || (hasScope ? 'Currency pending' : 'No scope');
  const converted = Boolean(defaultCurrency && reportingCurrency && defaultCurrency !== reportingCurrency);
  const title = handoffCurrency && !contractReportingCurrency
    ? `Ledger opened Lens with ${handoffCurrency}; contract reporting currency is still pending.`
    : converted
    ? `Ledger reporting currency ${reportingCurrency}; Book default ${defaultCurrency}`
    : reportingCurrency
      ? `Ledger reporting currency ${reportingCurrency}`
      : hasScope
        ? 'Ledger reporting currency pending for the selected Book/Shelf scope'
        : 'Select a Book/Shelf scope to resolve reporting currency';

  return (
    <div className={shell} title={title} data-ux-pattern="lens-reporting-currency-global-filter-control">
      <span className={label}>Currency</span>
      <button
        type="button"
        className={`${smallButton} cursor-default hover:border-border hover:text-text-muted`}
        aria-label="Lens reporting currency"
        disabled
      >
        {converted ? `${reportingCurrency}/${defaultCurrency}` : currencyLabel}
      </button>
    </div>
  );
}

export function LensBacktestDateRangeControl({ value, onChange }) {
  const range = value || {};
  const minDate = range.minDate || range.startDate || '';
  const maxDate = range.maxDate || range.endDate || '';
  const startDate = range.startDate || minDate;
  const endDate = range.endDate || maxDate;
  const startPct = percentWithin(startDate, minDate, maxDate);
  const endPct = percentWithin(endDate, minDate, maxDate);
  const title = minDate && maxDate
    ? `Backtesting date range. Actuals window: ${minDate} to ${maxDate}`
    : 'Backtesting date range';

  function patch(next) {
    onChange?.({ ...range, ...next });
  }

  return (
    <div className={shell} title={title} data-ux-pattern="lens-date-range-backtest-global-filter-control">
      <span className={label}>Backtest</span>
      <div className="relative h-1.5 w-24 shrink-0 rounded-full bg-bg" aria-hidden="true">
        <span
          className="absolute top-0 h-full rounded-full bg-accent-gold"
          style={{
            left: `${Math.min(startPct, endPct)}%`,
            right: `${100 - Math.max(startPct, endPct)}%`,
          }}
        />
      </div>
      <button type="button" className={smallButton} onClick={() => patch({ startDate: minDate })} disabled={!minDate}>
        MIN
      </button>
      <DateTextInput
        aria-label="Backtesting start date"
        className={dateInput}
        min={minDate}
        max={maxDate}
        value={startDate}
        onChange={(event) => patch({ startDate: event.target.value })}
      />
      <span className="text-text-dim">to</span>
      <DateTextInput
        aria-label="Backtesting end date"
        className={dateInput}
        min={minDate}
        max={maxDate}
        value={endDate}
        onChange={(event) => patch({ endDate: event.target.value })}
      />
      <button type="button" className={smallButton} onClick={() => patch({ endDate: maxDate })} disabled={!maxDate}>
        MAX
      </button>
    </div>
  );
}

export function LensLiveUpdateControl({
  sourceState,
  ledgerReady = false,
  refreshing = false,
  onRefresh,
}) {
  const strictLive = sourceState?.canClaimStrictRealBook === true || sourceState?.providerLiveProof === true;
  const status = refreshing ? 'Syncing' : strictLive ? 'Live' : ledgerReady ? 'Held' : 'Loading';
  const statusTone = strictLive
    ? 'border-accent-green/40 text-accent-green'
    : ledgerReady && !refreshing
      ? 'border-yellow-soft text-accent-gold'
      : 'border-border text-text-muted';

  return (
    <div
      className={shell}
      title={sourceState?.fallbackReason || sourceState?.warning || 'Refresh Lens from the active Ledger scope'}
      data-ux-pattern="lens-live-update-global-filter-control"
    >
      <span className={label}>Ledger</span>
      <span className={`rounded-md border bg-surface-el px-2 py-[0.25rem] text-[0.66rem] font-semibold uppercase tracking-[0.05em] ${statusTone}`}>
        {status}
      </span>
      <button
        type="button"
        className={smallButton}
        onClick={onRefresh}
        disabled={refreshing || typeof onRefresh !== 'function'}
      >
        Refresh
      </button>
    </div>
  );
}

export function LensOpenLedgerLink({ ledgerBaseUrl, selection }) {
  if (!ledgerBaseUrl) return null;
  const href = buildLedgerUrlWithBookScope(ledgerBaseUrl, selection);
  const hasScope = Boolean(selection?.bookId || selection?.shelfId);

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex min-h-[2rem] items-center gap-1.5 rounded-md border border-border bg-surface-raised px-2.5 py-1 text-[0.72rem] font-semibold text-text-muted no-underline hover:border-accent-gold hover:bg-[rgba(240,180,41,0.08)] hover:text-accent-gold"
      title={hasScope ? 'Open Ledger with the current Lens Book/Shelf scope' : 'Open Ledger'}
      data-ux-pattern="lens-open-ledger-global-filter-link"
    >
      Open Ledger
    </a>
  );
}

export const LensSingleScopeSelector = LensBookShelfScopeSelector;
export const LensActualsDateRangeControl = LensBacktestDateRangeControl;
